import { DeleteForever } from '@mui/icons-material';
import { Button, IconButton, Paper } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { StoreService } from '~/service/service.store';
import { FragranceOfferDto } from '~/shared/dto/admin/fragrance.dto';
import { Form } from '~/shared/form';
import { useService } from '~/shared/service/service.base';
import { FieldNumber } from '~/view/fields/field.number';
import { FieldSelect } from '~/view/fields/field.select';
import { FieldSwitch } from '~/view/fields/field.switch';
import { FieldText } from '~/view/fields/field.text';
import { Col, Row } from '~/view/view.box';

type FragranceOffersProps = {
	field: Form.Field<FragranceOfferDto[]>;
};
export const FragranceOffers = observer(({ field }: FragranceOffersProps) => {
	const [store] = useService(StoreService);
	store.useNames();

	const handleAdd = useCallback(() => {
		field.set([...field.value, { available: true } as FragranceOfferDto]);
	}, [field]);

	const handleRemove = useCallback(
		(idx: number) => {
			field.set(field.value.filter((_, i) => i !== idx));
		},
		[field],
	);

	const handleChange = useCallback(
		(idx: number, dto: FragranceOfferDto) => {
			const newOffers = [...field.value];
			newOffers[idx] = dto;
			field.set(newOffers);
		},
		[field],
	);

	return (
		<Col>
			<Col gap={2} mb={2}>
				{field.value.map((offer, idx) => (
					<FragranceOffer
						key={idx}
						offer={offer}
						onRemove={() => handleRemove(idx)}
						onChange={dto => handleChange(idx, dto)}
					/>
				))}
			</Col>
			<Button variant="outlined" onClick={handleAdd}>
				Add Offer
			</Button>
		</Col>
	);
});

// TODO: fetch currencies from backend
const currencyItems = [{ key: 'USD' }, { key: 'EUR' }, { key: 'GBP' }];

type FragranceOfferProps = {
	offer: FragranceOfferDto;
	onRemove: () => void;
	onChange: (dto: FragranceOfferDto) => void;
};

const FragranceOffer = observer(({ offer, onRemove, onChange }: FragranceOfferProps) => {
	const [store] = useService(StoreService);

	const form = Form.use(FragranceOfferDto, {
		name: 'FragranceOfferDto',
		initial: offer,
		onSubmit: v => onChange(v),
	});

	return (
		<Paper elevation={3} sx={{ p: 2 }}>
			<Col gap={2}>
				<FieldSelect items={store.names} field={form.$storeId} valueProp="id" labelProp="name" />
				<FieldText field={form.$buyUrl} />
				<Row gap={2} alignItems="flex-start" justifyContent="space-between">
					<FieldSelect field={form.$currency} items={currencyItems} labelProp="key" valueProp="key" />
					<FieldNumber field={form.$price} />
					<FieldNumber field={form.$discountPrice} />
				</Row>
				<Row gap={2} alignItems="flex-start" justifyContent="space-between">
					<FieldSwitch field={form.$available} />
					<IconButton onClick={onRemove}>
						<DeleteForever />
					</IconButton>
				</Row>
			</Col>
		</Paper>
	);
});
